import { ChevronDown, Edit2, Plus, Trash2, User } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { Transaction } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface SupportTabProps {
  transactions: Transaction[];
  searchTerm: string;
  exchangeRate: number;
  localCurrency: string;
  overseasCurrency: string;
  onEditTransaction: (t: Transaction) => void;
  onDeleteTransaction: (id: string) => void;
  onAddTransaction: () => void;
  isPrivacyMode: boolean;
}

export function SupportTab({ transactions, searchTerm, exchangeRate, localCurrency, overseasCurrency, onEditTransaction, onDeleteTransaction, onAddTransaction, isPrivacyMode }: SupportTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-lg text-zinc-900 dark:text-zinc-100">Support & Gifts (Overseas)</h3>
        <button onClick={onAddTransaction} className="w-10 h-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center shadow-lg">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="grid gap-4">
        {transactions
          .filter((t) => t.category === 'Overseas Expenses' && (t.note?.toLowerCase().includes(searchTerm.toLowerCase()) || t.relationship?.toLowerCase().includes(searchTerm.toLowerCase())))
          .sort((a, b) => {
            const isARelative = (a.relationship || '').toUpperCase() === 'RELATIVE';
            const isBRelative = (b.relationship || '').toUpperCase() === 'RELATIVE';
            if (isARelative && !isBRelative) return -1;
            if (!isARelative && isBRelative) return 1;
            return 0;
          })
          .map((gift) => (
            <motion.div 
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={gift.id} 
              onClick={() => setExpandedId(expandedId === gift.id ? null : gift.id)}
              className="bg-white dark:bg-zinc-900 p-5 rounded-[28px] border border-zinc-100 dark:border-zinc-800 shadow-sm relative overflow-hidden active:scale-[0.99] transition-all cursor-pointer"
            >
              <div className="relative z-10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border transition-colors",
                      expandedId === gift.id ? "bg-indigo-600 border-indigo-600" : "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-900/50"
                    )}>
                      <User className={cn("w-6 h-6 transition-colors", expandedId === gift.id ? "text-white" : "text-indigo-600 dark:text-indigo-400")} />
                    </div>
                    <div>
                      <h4 className="font-normal text-base text-zinc-900 dark:text-zinc-100 leading-tight">{gift.note}</h4>
                      <p className="text-[10px] font-normal text-zinc-400 uppercase tracking-wider mt-0.5">{gift.relationship}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={cn("text-base font-black text-zinc-900 dark:text-zinc-100 tracking-tight", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(gift.nairaAmount || 0, overseasCurrency)}</p>
                      <p className={cn("text-[10px] font-medium text-emerald-600 text-right", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(gift.amount, localCurrency)}</p>
                    </div> 
                    <ChevronDown className={cn("w-5 h-5 text-zinc-300 transition-transform duration-300", expandedId === gift.id && "rotate-180")} />
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {expandedId === gift.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-6">
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        {[
                          { label: 'Allowance', value: gift.allowance },
                          { label: 'Gift 1', value: gift.gift1 },
                          { label: 'Gift 2', value: gift.gift2 },
                        ].map((item, i) => (
                          <div key={i} className="bg-zinc-50/80 dark:bg-zinc-800/50 rounded-2xl p-3 border border-zinc-100 dark:border-zinc-700 text-center">
                            <p className="text-[9px] font-medium text-zinc-400 uppercase tracking-wider mb-1">{item.label}</p>
                            <p className={cn("text-xs font-medium text-zinc-700 dark:text-zinc-300", isPrivacyMode && "blur-sm select-none")}>{formatCurrency((item.value || 0) * exchangeRate, overseasCurrency)}</p>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-4 border-t border-dashed border-zinc-100 dark:border-zinc-800">
                        <button onClick={(e) => { e.stopPropagation(); onEditTransaction(gift); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium text-zinc-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors bg-white dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 shadow-sm">
                          <Edit2 className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onDeleteTransaction(gift.id); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium text-zinc-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors bg-white dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 shadow-sm">
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
      </div>
    </div>
  );
}